/* Manufacturing Insights — inline-SVG chart engine + HTML table builders.
 *
 * A faithful port of the original Flask dashboard's static/app.js rendering code
 * (barChart / comboChart / lineChart / forecastChart + helpers + table markup), so
 * the portal module renders identically. Framework-agnostic: each chart draws into a
 * mount DOM element (a Vue $ref), exactly like the original. No chart library — the
 * portal has none, and adding one would be pure risk for zero gain.
 *
 * All CSS classes used here (tick / axis / grid-line / barlabel / linelabel / drill-table
 * / lineage-table / auth / sys ...) are defined UN-scoped but namespaced under `.mi-wrap`
 * in ManufacturingInsights/Index.vue, so they style these JS-created nodes (Vue `scoped`
 * would not, since these elements are created outside the template).
 */
const NS = "http://www.w3.org/2000/svg";
// Palette matched to Foundry Insights' light-canvas chart tones (fi-* report), so the two
// reports read as one family: muted blue/green/amber/rust on white, not dark-mode neons.
export const C = {
  chrome: "#3d7a5a", duct: "#2f6db0", adi: "#c98a2b", gray: "#7a7f86",
  accent: "#2f6db0", molten: "#c0603a", good: "#2f7a4f", bad: "#b3402f",
  warn: "#c98a2b", muted: "#7a828b",
};

function El(t, a = {}, kids = []) {
  const e = document.createElementNS(NS, t);
  for (const k in a) e.setAttribute(k, a[k]);
  for (const c of [].concat(kids))
    if (c != null) e.appendChild(typeof c === "object" ? c : document.createTextNode(String(c)));
  return e;
}
function T(x, y, s, cls, anchor) {
  return El("text", { x, y, class: cls || "tick", "text-anchor": anchor || "middle" }, s);
}

export const abbr = n => n == null ? "—" : Math.abs(n) >= 1e6 ? (n / 1e6).toFixed(2) + "M" : Math.abs(n) >= 1e3 ? Math.round(n / 1e3) + "K" : Math.round(n).toString();
export const money = n => n == null ? "—" : "$" + abbr(n);
export const pct = f => f == null ? "—" : (f * 100).toFixed(1) + "%";
export const f2 = n => n == null ? "—" : Number(n).toFixed(2);
export const intc = n => n == null ? "—" : Math.round(n).toLocaleString();
export const usd0 = n => n == null ? "—" : (n < 0 ? "-$" : "$") + Math.round(Math.abs(n)).toLocaleString();
export const esc = s => String(s == null ? "" : s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
export const freshColor = s => s === "ok" ? C.good : s === "stale" ? C.bad : C.warn;
export const M3 = m => ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m] || m;
export const num1 = n => n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
export const prettyDept = s => String(s == null ? "" : s).split("_")
  .map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(" ").replace("Caps Dies", "Caps/Dies");

function niceMax(v) {
  if (v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const r = v / p;
  const s = r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10;
  return s * p;
}

// ---- generic bar chart (single or stacked) + optional goal ticks / per-bar color ----
export function barChart(mount, o) {
  const cats = o.cats, series = o.series;
  const W = Math.max(480, cats.length * 64), H = 280, m = { l: 46, r: 14, t: 16, b: 30 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / cats.length;
  const totals = cats.map((_, i) => series.reduce((s, se) => s + (se.data[i] || 0), 0));
  let maxV = Math.max(...totals, ...(o.goals || [0]));
  maxV = niceMax(maxV || 1);
  const y = v => m.t + ph * (1 - v / maxV);
  const g = El("svg", { viewBox: `0 0 ${W} ${H}`, role: "img" });
  for (let i = 0; i <= 4; i++) {
    const v = maxV * i / 4, yy = y(v);
    g.appendChild(El("line", { x1: m.l, y1: yy, x2: W - m.r, y2: yy, class: "grid-line" }));
    g.appendChild(T(m.l - 6, yy + 3, o.yfmt ? o.yfmt(v) : abbr(v), "tick", "end"));
  }
  cats.forEach((cat, i) => {
    const cx = m.l + i * band, bw = Math.min(46, band * 0.6), bx = cx + (band - bw) / 2;
    let acc = 0;
    series.forEach(se => {
      const val = se.data[i] || 0;
      if (val === 0 && series.length > 1) return;
      const h = ph * val / maxV, yy = y(acc + val);
      let fill = se.color;
      if (o.colorByGoal && o.goals) {
        const ok = o.goalDir === "high" ? (val >= o.goals[i]) : (val <= o.goals[i]);
        fill = ok ? C.good : C.bad;
      }
      g.appendChild(El("rect", { x: bx, y: yy, width: bw, height: Math.max(0, h), rx: 2, fill, opacity: 0.92 }));
      acc += val;
    });
    if (o.valOnTop !== false) g.appendChild(T(cx + band / 2, y(acc) - 5, o.valfmt ? o.valfmt(totals[i]) : abbr(totals[i]), "barlabel"));
    if (o.goals) {
      const gy = y(o.goals[i]);
      g.appendChild(El("line", { x1: bx - 3, y1: gy, x2: bx + bw + 3, y2: gy, stroke: C.warn, "stroke-width": 2, "stroke-dasharray": "4 2" }));
    }
    g.appendChild(T(cx + band / 2, H - m.b + 14, cat, "tick"));
    if (o.onBar) {
      const hit = El("rect", { x: cx, y: m.t, width: band, height: ph, fill: "transparent", style: "cursor:pointer" });
      hit.addEventListener("click", () => o.onBar(i));
      g.appendChild(hit);
    }
  });
  g.appendChild(El("line", { x1: m.l, y1: m.t, x2: m.l, y2: H - m.b, class: "axis" }));
  g.appendChild(El("line", { x1: m.l, y1: H - m.b, x2: W - m.r, y2: H - m.b, class: "axis" }));
  mount.innerHTML = ""; mount.appendChild(g);
}

// ---- combo: bars ($) + line/dots on secondary axis (scrap %) ----
export function comboChart(mount, o) {
  const cats = o.cats, W = Math.max(480, cats.length * 64), H = 280, m = { l: 46, r: 44, t: 16, b: 30 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b, band = pw / cats.length;
  const maxBar = niceMax(Math.max(...o.bars.map(v => v || 0), 1));
  const maxLine = niceMax(Math.max(...o.line.map(v => v || 0), 0.01));
  const yB = v => m.t + ph * (1 - v / maxBar), yL = v => m.t + ph * (1 - v / maxLine);
  const g = El("svg", { viewBox: `0 0 ${W} ${H}`, role: "img" });
  for (let i = 0; i <= 4; i++) {
    const yy = m.t + ph * i / 4;
    g.appendChild(El("line", { x1: m.l, y1: yy, x2: W - m.r, y2: yy, class: "grid-line" }));
    g.appendChild(T(m.l - 6, yy + 3, money(maxBar * (4 - i) / 4), "tick", "end"));
    g.appendChild(T(W - m.r + 6, yy + 3, pct(maxLine * (4 - i) / 4), "tick", "start"));
  }
  cats.forEach((cat, i) => {
    const cx = m.l + i * band, bw = Math.min(40, band * 0.55), bx = cx + (band - bw) / 2;
    const v = o.bars[i] || 0;
    g.appendChild(El("rect", { x: bx, y: yB(v), width: bw, height: Math.max(0, ph * v / maxBar), rx: 2, fill: C.molten, opacity: 0.85 }));
    g.appendChild(T(cx + band / 2, yB(v) - 5, money(v), "barlabel"));
    g.appendChild(T(cx + band / 2, H - m.b + 14, cat, "tick"));
  });
  const pts = cats.map((_, i) => [m.l + i * band + band / 2, yL(o.line[i] || 0)]);
  g.appendChild(El("polyline", { points: pts.map(p => p.join(",")).join(" "), fill: "none", stroke: C.accent, "stroke-width": 2 }));
  pts.forEach((p, i) => {
    g.appendChild(El("circle", { cx: p[0], cy: p[1], r: 3.2, fill: C.accent }));
    const barTopY = yB(o.bars[i] || 0);
    const ly = (p[1] < barTopY - 14) ? p[1] - 8 : p[1] + 16;
    g.appendChild(T(p[0], ly, pct(o.line[i]), "linelabel"));
  });
  g.appendChild(El("line", { x1: m.l, y1: m.t, x2: m.l, y2: H - m.b, class: "axis" }));
  g.appendChild(El("line", { x1: m.l, y1: H - m.b, x2: W - m.r, y2: H - m.b, class: "axis" }));
  mount.innerHTML = ""; mount.appendChild(g);
}

// ---- line chart (weekly/monthly trend + goal line) ----
export function lineChart(mount, o) {
  const cats = o.cats, data = o.data;
  const W = Math.max(560, cats.length * 24), H = 280, m = { l: 46, r: 16, t: 16, b: 36 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b;
  const vals = data.filter(v => v != null);
  const maxV = niceMax(Math.max(...(vals.length ? vals : [1]), o.goal || 0));
  const x = i => m.l + (cats.length <= 1 ? pw / 2 : pw * i / (cats.length - 1));
  const y = v => m.t + ph * (1 - v / maxV);
  const g = El("svg", { viewBox: `0 0 ${W} ${H}`, role: "img" });
  for (let k = 0; k <= 4; k++) {
    const yy = m.t + ph * k / 4;
    g.appendChild(El("line", { x1: m.l, y1: yy, x2: W - m.r, y2: yy, class: "grid-line" }));
    g.appendChild(T(m.l - 6, yy + 3, (o.yfmt ? o.yfmt(maxV * (4 - k) / 4) : abbr(maxV * (4 - k) / 4)), "tick", "end"));
  }
  if (o.goal) {
    const gy = y(o.goal);
    g.appendChild(El("line", { x1: m.l, y1: gy, x2: W - m.r, y2: gy, stroke: C.warn, "stroke-width": 1.5, "stroke-dasharray": "5 3" }));
    g.appendChild(T(W - m.r, gy - 4, "goal " + f2(o.goal), "barlabel", "end"));
  }
  const pts = cats.map((c, i) => [x(i), y(data[i] || 0)]);
  g.appendChild(El("polyline", { points: pts.map(p => p.join(",")).join(" "), fill: "none", stroke: C.accent, "stroke-width": 2 }));
  pts.forEach(p => g.appendChild(El("circle", { cx: p[0], cy: p[1], r: 2.6, fill: C.accent })));
  if (o.onPoint) { const bw = pw / Math.max(1, cats.length); cats.forEach((c, i) => { const hit = El("rect", { x: x(i) - bw / 2, y: m.t, width: bw, height: ph, fill: "transparent", style: "cursor:pointer" }); hit.addEventListener("click", () => o.onPoint(i)); g.appendChild(hit); }); }
  const step = Math.max(1, Math.ceil(cats.length / 8));
  cats.forEach((c, i) => { if (i % step === 0 || i === cats.length - 1) g.appendChild(T(x(i), H - m.b + 14, c, "tick")); });
  g.appendChild(El("line", { x1: m.l, y1: m.t, x2: m.l, y2: H - m.b, class: "axis" }));
  g.appendChild(El("line", { x1: m.l, y1: H - m.b, x2: W - m.r, y2: H - m.b, class: "axis" }));
  mount.innerHTML = ""; mount.appendChild(g);
}

// ---- multi-series line chart (overlay several trends + optional goal line + legend) ----
// o.series = [{ key, color, data:[] }], o.cats, o.goal, o.yfmt. Used for the Shipping tab's
// System-vs-Real ship-on-time overlay (the gap between the two lines is the whole point).
export function multiLineChart(mount, o) {
  const cats = o.cats, series = o.series || [];
  const W = Math.max(560, cats.length * 24), H = 300, m = { l: 46, r: 16, t: 30, b: 36 };
  const pw = W - m.l - m.r, ph = H - m.t - m.b;
  const allVals = series.reduce((a, s) => a.concat((s.data || []).filter(v => v != null)), []);
  const maxV = niceMax(Math.max(...(allVals.length ? allVals : [1]), o.goal || 0));
  const x = i => m.l + (cats.length <= 1 ? pw / 2 : pw * i / (cats.length - 1));
  const y = v => m.t + ph * (1 - v / maxV);
  const g = El("svg", { viewBox: `0 0 ${W} ${H}`, role: "img" });
  for (let k = 0; k <= 4; k++) {
    const yy = m.t + ph * k / 4;
    g.appendChild(El("line", { x1: m.l, y1: yy, x2: W - m.r, y2: yy, class: "grid-line" }));
    g.appendChild(T(m.l - 6, yy + 3, (o.yfmt ? o.yfmt(maxV * (4 - k) / 4) : abbr(maxV * (4 - k) / 4)), "tick", "end"));
  }
  if (o.goal) {
    const gy = y(o.goal);
    g.appendChild(El("line", { x1: m.l, y1: gy, x2: W - m.r, y2: gy, stroke: C.warn, "stroke-width": 1.5, "stroke-dasharray": "5 3" }));
    g.appendChild(T(W - m.r, gy - 4, "goal " + f2(o.goal), "barlabel", "end"));
  }
  series.forEach(se => {
    const col = se.color || C.accent;
    const pts = cats.map((c, i) => [x(i), y(se.data[i] || 0)]);
    g.appendChild(El("polyline", { points: pts.map(p => p.join(",")).join(" "), fill: "none", stroke: col, "stroke-width": 2 }));
    pts.forEach(p => g.appendChild(El("circle", { cx: p[0], cy: p[1], r: 2.6, fill: col })));
  });
  // legend across the top margin band
  let lx = m.l;
  series.forEach(se => {
    const col = se.color || C.accent, label = se.key || "";
    g.appendChild(El("line", { x1: lx, y1: 12, x2: lx + 16, y2: 12, stroke: col, "stroke-width": 3 }));
    g.appendChild(T(lx + 20, 15, label, "linelabel", "start"));
    lx += 20 + label.length * 6.4 + 18;
  });
  if (o.onPoint) { const bw = pw / Math.max(1, cats.length); cats.forEach((c, i) => { const hit = El("rect", { x: x(i) - bw / 2, y: m.t, width: bw, height: ph, fill: "transparent", style: "cursor:pointer" }); hit.addEventListener("click", () => o.onPoint(i)); g.appendChild(hit); }); }
  const step = Math.max(1, Math.ceil(cats.length / 8));
  cats.forEach((c, i) => { if (i % step === 0 || i === cats.length - 1) g.appendChild(T(x(i), H - m.b + 14, c, "tick")); });
  g.appendChild(El("line", { x1: m.l, y1: m.t, x2: m.l, y2: H - m.b, class: "axis" }));
  g.appendChild(El("line", { x1: m.l, y1: H - m.b, x2: W - m.r, y2: H - m.b, class: "axis" }));
  mount.innerHTML = ""; mount.appendChild(g);
}

export const fcFmtY = (unit, v) =>
  unit === "usd" ? abbr(v) : unit === "usdhr" ? "$" + Math.round(v) : unit === "pct" ? (v * 100).toFixed(0) + "%" : abbr(v);

// ---- forecasting: history + dashed projection + confidence band ----
export function forecastChart(mount, o) {
  const H = o.history.map(h => ({ t: h.year * 12 + h.month, v: h.value, m: h.month, y: h.year }));
  const F = o.forecast.map(f => ({ t: f.year * 12 + f.month, yhat: f.yhat, lo: f.lo, hi: f.hi, m: f.month, y: f.year }));
  const seq = H.concat(F.map(f => ({ t: f.t, m: f.m, y: f.y })));
  const W = Math.max(560, seq.length * 22), Ht = 280, mg = { l: 52, r: 14, t: 16, b: 34 };
  const pw = W - mg.l - mg.r, ph = Ht - mg.t - mg.b;
  const vals = []; H.forEach(h => { if (h.v != null) vals.push(h.v); });
  F.forEach(f => vals.push(f.hi, f.lo, f.yhat)); if (o.goal != null) vals.push(o.goal);
  const maxV = niceMax(Math.max(...vals, 0.0001));
  const idx = {}; seq.forEach((a, i) => idx[a.t] = i);
  const x = i => mg.l + (seq.length <= 1 ? pw / 2 : pw * i / (seq.length - 1));
  const y = v => mg.t + ph * (1 - v / maxV);
  const g = El("svg", { viewBox: `0 0 ${W} ${Ht}`, role: "img" });
  for (let k = 0; k <= 4; k++) {
    const yy = mg.t + ph * k / 4;
    g.appendChild(El("line", { x1: mg.l, y1: yy, x2: W - mg.r, y2: yy, class: "grid-line" }));
    g.appendChild(T(mg.l - 6, yy + 3, fcFmtY(o.unit, maxV * (4 - k) / 4), "tick", "end"));
  }
  if (F.length) {
    const top = F.map(f => [x(idx[f.t]), y(f.hi)]);
    const bot = F.map(f => [x(idx[f.t]), y(f.lo)]).reverse();
    g.appendChild(El("polygon", { points: top.concat(bot).map(p => p.join(",")).join(" "), fill: C.accent, opacity: 0.13 }));
  }
  if (o.goal != null) {
    const gy = y(o.goal);
    g.appendChild(El("line", { x1: mg.l, y1: gy, x2: W - mg.r, y2: gy, stroke: C.warn, "stroke-width": 1.5, "stroke-dasharray": "5 3" }));
  }
  const hp = H.filter(h => h.v != null).map(h => [x(idx[h.t]), y(h.v)]);
  if (hp.length) g.appendChild(El("polyline", { points: hp.map(p => p.join(",")).join(" "), fill: "none", stroke: C.accent, "stroke-width": 2 }));
  let fp = F.map(f => [x(idx[f.t]), y(f.yhat)]);
  if (hp.length && fp.length) fp = [hp[hp.length - 1]].concat(fp);
  if (fp.length) g.appendChild(El("polyline", { points: fp.map(p => p.join(",")).join(" "), fill: "none", stroke: C.accent, "stroke-width": 2, "stroke-dasharray": "5 3" }));
  const step = Math.max(1, Math.ceil(seq.length / 10));
  seq.forEach((a, i) => {
    if (i % step === 0 || i === seq.length - 1)
      g.appendChild(T(x(i), Ht - mg.b + 14, M3(a.m) + (a.m === 1 ? "'" + String(a.y).slice(2) : ""), "tick"));
  });
  g.appendChild(El("line", { x1: mg.l, y1: mg.t, x2: mg.l, y2: Ht - mg.b, class: "axis" }));
  g.appendChild(El("line", { x1: mg.l, y1: Ht - mg.b, x2: W - mg.r, y2: Ht - mg.b, class: "axis" }));
  mount.innerHTML = ""; mount.appendChild(g);
}

// ---- overview card formatting ----
export const ovFmt = (unit, v) => {
  if (v == null) return "—";
  if (unit === "usd") return money(v);
  if (unit === "usdhr") return "$" + Number(v).toFixed(2) + "/hr";
  if (unit === "pct") return (v * 100).toFixed(1) + "%";
  return String(v);
};

// ================= HTML table builders (returned as strings, used via v-html) =========
export function lateShipTableHtml(lt) {
  if (!lt || !lt.length) return '<div class="hint">Awaiting first Shipping sync.</div>';
  const body = lt.map((r, i) =>
    `<tr data-dept="${esc(r.Department)}" class="clickrow"><td class="rank">${i + 1}</td><td><b>${esc(prettyDept(r.Department))}</b></td>` +
    `<td class="num">${num1(r.LateOrders)}</td></tr>`).join("");
  return `<table class="drill-table"><thead><tr><th class="rank">#</th><th>Department</th>` +
    `<th class="num">Late Orders</th></tr></thead><tbody>${body}</tbody></table>`;
}

function scrapTable(title, rows, keyHdr, showEvents) {
  const body = (rows || []).map((r, i) => {
    const desc = r.Description && String(r.Description).trim() && r.Description !== r.key
      ? `<div class="cust">${esc(r.Description)}</div>` : "";
    return `<tr><td class="rank">${i + 1}</td><td><b>${esc(r.key)}</b>${desc}</td>` +
      (showEvents ? `<td class="num">${intc(r.ScrapEvents)}</td>` : "") +
      `<td class="num">${intc(r.BadQty)}</td><td class="num">${usd0(r.EstScrapCost)}</td></tr>`;
  }).join("");
  return `<div class="scrap-col"><h3>${esc(title)}</h3>` +
    `<table class="drill-table scrap-mini"><thead><tr><th class="rank">#</th><th>${esc(keyHdr)}</th>` +
    (showEvents ? `<th class="num">Events</th>` : "") +
    `<th class="num">Bad Qty</th><th class="num">Est $</th></tr></thead>` +
    `<tbody>${body || `<tr><td colspan="5" class="hint">No data</td></tr>`}</tbody></table></div>`;
}

export function scrapTopHtml(st) {
  if (!st) return '<div class="hint">Awaiting first scrap sync.</div>';
  return `<div class="scrap-grid">` +
    scrapTable("Top Defect Reasons", st.reasons, "Reason", true) +
    scrapTable("Worst Parts", st.parts, "Part", false) +
    scrapTable("By Work-Center", st.resources, "Resource", true) +
    `</div>`;
}

// RMA "why parts came back" breakdown tables (reasons / departments / parts / types).
// Rows carry: key, Description, Dept, RmaCount, ReturnedQty, Era (from rma/top).
function rmaTable(title, rows, keyHdr, o) {
  o = o || {};
  const body = (rows || []).map((r, i) => {
    const desc = r.Description && String(r.Description).trim() && r.Description !== r.key
      ? `<div class="cust">${esc(r.Description)}</div>` : "";
    return `<tr><td class="rank">${i + 1}</td><td><b>${esc(r.key)}</b>${desc}</td>` +
      (o.showDept ? `<td>${esc(r.Dept || "")}</td>` : "") +
      `<td class="num">${intc(r.RmaCount)}</td>` +
      (o.showQty ? `<td class="num">${num1(r.ReturnedQty)}</td>` : "") + `</tr>`;
  }).join("");
  const cols = 2 + (o.showDept ? 1 : 0) + 1 + (o.showQty ? 1 : 0);
  return `<div class="scrap-col"><h3>${esc(title)}</h3>` +
    `<table class="drill-table scrap-mini"><thead><tr><th class="rank">#</th><th>${esc(keyHdr)}</th>` +
    (o.showDept ? `<th>Dept</th>` : "") + `<th class="num">RMAs</th>` +
    (o.showQty ? `<th class="num">Qty</th>` : "") + `</tr></thead>` +
    `<tbody>${body || `<tr><td colspan="${cols}" class="hint">No data</td></tr>`}</tbody></table></div>`;
}

export function rmaTopHtml(rt) {
  if (!rt) return '<div class="hint">Awaiting first RMA sync.</div>';
  return `<div class="scrap-grid">` +
    rmaTable("Return Reasons — historical (through 2024)", rt.reasonsHistorical, "Reason", { showDept: true }) +
    rmaTable("By Responsible Department — historical (through 2024)", rt.departments, "Department", {}) +
    rmaTable("Most-Returned Parts (current)", rt.parts, "Part", { showQty: true }) +
    rmaTable("By RMA Type (current)", rt.types, "Type", {}) +
    `</div>`;
}

export function invoiceTableHtml(d) {
  const inv = (d && d.invoices) || [];
  if (!inv.length) return '<div class="hint">No invoice detail available for this month.</div>';
  const body = inv.map((r, i) =>
    `<tr class="${r.type === "M" ? "credit" : ""}"><td class="rank">${i + 1}</td>` +
    `<td>${esc(r.invoice)}</td><td>${esc(r.date || "")}</td>` +
    `<td class="cust">${esc(r.customer || "")}${r.type === "M" ? " <em>(credit memo)</em>" : ""}</td>` +
    `<td class="num">${usd0(r.amount)}</td></tr>`).join("");
  const others = d.others_count > 0
    ? `<tr class="others"><td></td><td colspan="3">+ ${intc(d.others_count)} more invoices</td>` +
      `<td class="num">${usd0(d.others_amount)}</td></tr>` : "";
  return `<table class="drill-table"><thead><tr><th class="rank">#</th><th>Invoice</th><th>Date</th>` +
    `<th class="cust">Customer</th><th class="num">Parts amount</th></tr></thead>` +
    `<tbody>${body}${others}</tbody>` +
    `<tfoot><tr><td colspan="4">Month total — ${intc(d.invoice_count)} invoices</td>` +
    `<td class="num">${usd0(d.total)}</td></tr></tfoot></table>`;
}

// Returned-parts detail for one month's RMAs (the RMA month drill-down).
export function rmaDetailHtml(d) {
  const rows = (d && d.rmas) || [];
  if (!rows.length) return '<div class="hint">No RMA detail available for this month.</div>';
  const body = rows.map((r, i) =>
    `<tr><td class="rank">${i + 1}</td><td>${esc(r.rma)}</td><td>${esc(r.date || "")}</td>` +
    `<td class="cust">${esc(r.customer || "")}</td>` +
    `<td><b>${esc(r.part || "")}</b>${r.partDesc ? `<div class="cust">${esc(r.partDesc)}</div>` : ""}</td>` +
    `<td class="num">${num1(r.qty)}</td><td class="cust">${esc(r.reason || "")}</td>` +
    `<td>${esc(r.type || "")}</td><td>${esc(r.status || "")}</td></tr>`).join("");
  return `<table class="drill-table"><thead><tr><th class="rank">#</th><th>RMA</th><th>Date</th>` +
    `<th class="cust">Customer</th><th>Part</th><th class="num">Qty</th><th class="cust">Reason for return</th><th>Type</th><th>Status</th>` +
    `</tr></thead><tbody>${body}</tbody>` +
    `<tfoot><tr><td colspan="9">${intc(d.count)} RMA line(s) this month</td></tr></tfoot></table>`;
}

export function chromeDetailHtml(d) {
  const g = (d && d.grades) || [];
  if (!g.length) return '<div class="hint">No chrome detail for this month.</div>';
  const body = g.map(r => {
    const note = r.PriceAnnotation ? `<div class="cust">${esc(r.PriceAnnotation)}</div>` : "";
    return `<tr><td><b>${esc(r.CarbonGrade)}</b>${note}</td>` +
      `<td class="num">${intc(r.ChromeUsedLbs)}</td>` +
      `<td class="num">$${Number(r.UnitPriceUsdLb || 0).toFixed(3)}</td>` +
      `<td class="num">${usd0(r.ChromeCostUsd)}</td></tr>`;
  }).join("");
  const total = g.reduce((s, r) => s + (r.ChromeCostUsd || 0), 0);
  const totLbs = g.reduce((s, r) => s + (r.ChromeUsedLbs || 0), 0);
  return `<table class="drill-table"><thead><tr><th>Carbon grade</th><th class="num">Used (lbs)</th>` +
    `<th class="num">$/lb</th><th class="num">Cost</th></tr></thead><tbody>${body}</tbody>` +
    `<tfoot><tr><td>Month total</td><td class="num">${intc(totLbs)}</td><td class="num"></td>` +
    `<td class="num">${usd0(total)}</td></tr></tfoot></table>`;
}

export function inventoryHtml(inv) {
  const rows = (inv && inv.rows) || [];
  if (!rows.length) return '<div class="hint">Awaiting first inventory sync.</div>';
  const when = inv.count_period ? " — count of " + esc(inv.count_period) : "";
  const body = rows.map(r =>
    `<tr><td><b>${esc(r.MaterialName)}</b></td><td>${esc(r.TableSection)}</td>` +
    `<td class="num">${num1(r.TotalQty)}</td><td>${esc(r.UOM || r.PackageSize || "")}</td></tr>`).join("");
  return `<table class="drill-table"><thead><tr><th>Material</th><th>Section</th>` +
    `<th class="num">On hand</th><th>UOM</th></tr></thead><tbody>${body}</tbody></table>` +
    `<p class="hint">${rows.length} materials${when}</p>`;
}

function fileLineHtml(prov, m) {
  if (!m.source_file) return "";
  const when = m.ingested_at ? " · loaded " + esc(String(m.ingested_at).replace("T", " "))
    : (prov && prov.representative_files ? " <em>(representative)</em>" : "");
  return `<span class="lineage-file">${esc(m.source_file)}${when}</span>`;
}

// "Open in SharePoint" link for spreadsheet-sourced metrics (those carrying source_url).
function srcUrlHtml(m) {
  if (!m.source_url) return "";
  return `<a class="lineage-src-link" href="${esc(m.source_url)}" target="_blank" rel="noopener">Open in SharePoint &#8599;</a>`;
}

function asOfHtml(m) {
  const fr = m.freshness;
  if (!fr) return "—";
  const dot = `<span style="color:${freshColor(fr.status)}">&#9679;</span> `;
  const age = fr.age_days != null ? ` (${fr.age_days}d)` : "";
  return dot + esc(fr.latest || "not started") + age;
}

export function lineageHtml(prov) {
  if (!prov || !prov.metrics) return '<div class="hint">Loading…</div>';
  const rows = prov.metrics.map(m => {
    const recon = m.reconciled ? ` <span class="auth RECONCILED">RECONCILED</span>` : "";
    const nd = (m.displayed === false) ? ` — <em>ingested, not yet charted</em>` : "";
    return `<tr class="${m.displayed === false ? "notdisplayed" : ""}">` +
      `<td class="metric">${esc(m.label)}</td>` +
      `<td><span class="sys">${esc(m.source_system)}</span></td>` +
      `<td><span class="auth ${m.authority}">${m.authority}</span>${recon}</td>` +
      `<td class="src"><code>${esc(m.source_object)}</code>${srcUrlHtml(m)}${fileLineHtml(prov, m)}</td>` +
      `<td class="asof">${asOfHtml(m)}</td>` +
      `<td class="basis">${esc(m.basis)}${nd}</td></tr>`;
  }).join("");
  return `<table class="lineage-table"><thead><tr><th>Metric</th><th>System</th><th>Authority</th>` +
    `<th>Source object</th><th>As of</th><th>How it's established</th></tr></thead>` +
    `<tbody>${rows}</tbody></table>`;
}

// popover inner HTML for a metric (positioning handled by the component)
export function provPopoverHtml(prov, m) {
  const recon = (m.reconciled && m.reconciliation)
    ? `<div class="row"><b>Reconciliation:</b> ${esc(m.reconciliation.status || "—")}` +
      `${m.reconciliation.variance_pct != null ? " (" + (m.reconciliation.variance_pct * 100).toFixed(1) + "%)" : ""}</div>`
    : "";
  const reconChip = m.reconciled ? ` <span class="auth RECONCILED">RECONCILED</span>` : "";
  const fileRow = m.source_file ? `<div class="row"><b>File:</b> ${fileLineHtml(prov, m)}</div>` : "";
  return `<button type="button" class="pop-close" aria-label="close">&times;</button>` +
    `<h3>${esc(m.label)} <span class="sys">${esc(m.source_system)}</span> ` +
    `<span class="auth ${m.authority}">${m.authority}</span>${reconChip}</h3>` +
    `<div class="row"><b>Source:</b> <code>${esc(m.source_object)}</code></div>` +
    fileRow +
    `<div class="row"><b>As of:</b> ${asOfHtml(m)}</div>` +
    recon +
    `<div class="basis">${esc(m.basis)}</div>`;
}
